import React, {
  Fragment,
  useContext,
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback
} from 'react';
import { withRouter } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import isEmpty from 'lodash/isEmpty';
import { Formik } from 'formik';
import moment, { isMoment } from 'moment';
import { message } from 'antd';
import configForFE from 'common-pickapier/configForFE';
import i18n from '../../../i18n';
import isNil from 'lodash/isNil';

import { AuthContext } from 'context/AuthProvider';
import { EmailVerificationContext } from 'context/EmailVerificationProvider';

import Button from 'components/UI/Antd/Button/Button';

import RenderReservationForm from './RenderReservationForm';
import WizardDrawer from './WizardDrawer';
import ReservationResults from './ReservationResults';

import boaterService from 'services/boater.js';
import reservationService from 'services/reservation.js';

import { checkEmptyProps } from 'library/helpers/papUtils';
import {
  getVesselReserveSchema,
  getVesselReserveWithNameSchema
} from 'library/helpers/formUtils';
import { setStateToUrl, getStateFromUrl } from 'library/helpers/url_handler';
import { onDownload } from 'library/helpers/utils';
import ReactPixel from 'services/facebookPixel';

import {
  updateUserProperties,
  getVesselType,
  getFormattedCheckPriceEvent,
  getFormattedPopeyePromotionType
} from 'library/helpers/amplitude';

import {
  RESERVATION_PAGE,
  MESSAGES_PAGE,
  MARINA_PAGE
} from 'settings/constant';

import { Wrapper, DownloadButtonWrapper, Error } from './Reservation.style.js';
import { useSessionStorage } from 'library/hooks/useSessionStorage';

import {
  SIGN_UP_AUTH,
  SIGN_IN_AUTH,
  GUEST_RESERVATION
} from '../MembershipsWizard/MembershipsWizard';

import useAmplitude from 'library/hooks/useAmplitude';
import useReservationCalculator from './useReservationCalculator';

message.config({
  top: 100,
  duration: 7,
  maxCount: 1
});

// Outside of the component, since it is used to gather information of an amplitude event until refreshing the page.
const timestampForAmplitude = moment().format();

const ReservationForm = ({
  match,
  history,
  location,
  marina,
  isMessaging,
  changePricing,
  focusedDateInput,
  setFocusedDateInput,
  date,
  setDate,
  getDateFromSearchParams,
  promotions
}) => {
  const { t } = useTranslation();
  const { sendToAmplitude } = useAmplitude();
  const {
    loggedIn,
    user,
    refreshData,
    getStatisticsForBoater,
    selectedBoaterVessel,
    selectBoaterVessel
  } = useContext(AuthContext);
  const { onVerify, setModalCloseCallback } = useContext(
    EmailVerificationContext
  );

  const selectRef = useRef(null);
  const reservationFormRef = useRef();

  const vesselHullTypes = useMemo(() => {
    return configForFE.VESSEL_HULL_TYPES.reduce(
      (map, type) => ({ ...map, [type]: t(type) }),
      {}
    );
  }, [t]);

  const userVessels = user?.vessels;

  const vessels = useMemo(() => {
    return user && !isEmpty(user) && userVessels ? userVessels : [];
  }, [user, userVessels]);

  const vesselOptions = useMemo(() => {
    return vessels.map(({ id, name }) => ({
      label: name || t('VESSEL_NAME_UNNAMED'),
      value: id
    }));
  }, [vessels, t]);

  const { id: marina_id, name, marina_settings, pricing_link } = marina;
  const { instant_booking, reservation_limits } = marina_settings;
  const {max_vessel_beam, max_vessel_draft, max_vessel_length} = reservation_limits['short'];
  const maxVesselLengths={max_vessel_beam, max_vessel_draft, max_vessel_length };

  const vesselReserveSchema = getVesselReserveSchema(i18n, marina_settings.marina_requirements.short.initial_inquiry.includes("vessel_hull_type"), maxVesselLengths);
  const vesselReserveWithNameSchema = getVesselReserveWithNameSchema(i18n,marina_settings.marina_requirements.short.initial_inquiry.includes("vessel_hull_type"), maxVesselLengths);

  const boaterAttachments = user ? user.attachments || [] : [];

  // date controls
  const [startDateInvalid, setStartDateInvalid] = useState(false);
  const [endDateInvalid, setEndDateInvalid] = useState(false);
  const [dateError, setDateError] = useState(null);

  // reservation creation
  const [reservationId, setReservationId] = useState(null);
  const [successOpened, setSuccessOpened] = useState(false);
  const [infoDialogShown, setInfoDialogShown] = useState(false);
  const [reservationCreationError, setReservationCreationError] =
    useState(null);
  const [requestedRedeemNightsAmount, setRequestedRedeemNightsAmount] =
    useState(null);

  // authentication
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [auth, setAuth] = useState(SIGN_UP_AUTH);

  // vessel selection controls
  const [open, setOpen] = useState(false);
  const [addNew, setAddNew] = useState(false);
  const [emptyVesselSelected, setEmptyVesselSelected] = useState(false);
  const [noNameVesselSelected, setNoNameVesselSelected] = useState(false);

  // reservation form
  const [values, _setValues] = useState({
    hull_type: null,
    length: '',
    draft: '',
    beam: '',
    requested_redeem_nights_amount: null,
    vessel_name: ''
  });
  const valuesStateRef = useRef(values);
  const setValues = data => {
    valuesStateRef.current = data;
    _setValues(data);
  };
  const [isTEMConnected, setIsTEMConnected] = useState(false);
  const [isHomeMarinaReservationAttempt, setIsHomeMarinaReservationAttempt] =
    useState(false);
  const [attachmentIds, setAttachmentIds] = useState([]);

  // For a new vessel and for a vessel without required length, beam, draft info.
  const [vesselMissingDetails, setVesselMissingDetails] = useState({});

  const onMaxNightsToRedeemLessThanRequested = useCallback(maxNights => {
    if(reservationFormRef.current) {
      const {values, setFieldValue} = reservationFormRef.current;
      setFieldValue(
          'requested_redeem_nights_amount',
          maxNights
      );
    }
  }, [reservationFormRef]);

  const {
    calculatedPrice,
    redeemNightsOptions
  } = useReservationCalculator({
    startDate: date.setStartDate,
    endDate: date.setEndDate,
    requestedRedeemNightsAmount,
    isTEMConnected,
    marinaId: marina_id,
    values,
    emptyVesselSelected,
    vesselMissingDetails,
    onMaxNightsToRedeemLessThanRequested
  });

  useEffect(() => {
    changePricing(calculatedPrice)
  }, [changePricing, calculatedPrice]);

  const amplitudeData = {
    id: timestampForAmplitude,
    marina_name: marina.name,
    marina_id: marina.id,
    marina_country: marina.country,
    association: marina.association.name
  };

  const [numberOfMarinasViewed, setNumberOfMarinasViewed] = useSessionStorage(
    'numberOfMarinasViewed',
    []
  );
  useEffect(() => {
    if (loggedIn && selectedBoaterVessel && marina_id && vessels.length) {
      // initial values for signed in user
      const currentVessel = vessels.filter(
        ({ id }) => id === selectedBoaterVessel
      )[0];
      const {
        hull_type,
        length,
        draft,
        beam,
        name: vessel_name,
        id: vessel_id,
        attachments: vesselAttachments
      } = currentVessel;
      const initialValues = {
        vessel_id,
        vessel_name: vessel_name || '',
        hull_type: hull_type || null,
        length: length || '',
        draft: draft || '',
        beam: beam || '',
        requested_redeem_nights_amount:
          values.requested_redeem_nights_amount || null
      };
      setEmptyVesselSelected(!length || !draft || !beam || !hull_type);
      setNoNameVesselSelected(!vessel_name);
      setValues(initialValues);
      setIsTEMConnected(Boolean(currentVessel.memberships.length));
      setIsHomeMarinaReservationAttempt(
        currentVessel.home_marina_id === marina_id
      );

      // set attachments
      const documents = {
        SKIPPER_PASSPORT: boaterAttachments
          ? boaterAttachments.filter(
              a => a.attachment_type === 'SKIPPER_PASSPORT'
            )[0]
          : null,
        VESSEL_LICENSE: vesselAttachments
          ? vesselAttachments.filter(
              a => a.attachment_type === 'VESSEL_LICENSE'
            )[0]
          : null,
        VESSEL_INSURANCE: vesselAttachments
          ? vesselAttachments.filter(
              a => a.attachment_type === 'VESSEL_INSURANCE'
            )[0]
          : null,
        SKIPPER_ID_LICENSE: boaterAttachments
          ? boaterAttachments.filter(
              a => a.attachment_type === 'SKIPPER_ID_LICENSE'
            )[0]
          : null
      };
      setAttachmentIds(
        Object.keys(documents)
          .filter(key => documents[key] && documents[key].shared_by_default)
          .map(doc => documents[doc].id)
      );
    }
  }, [selectedBoaterVessel, marina_id, vessels, loggedIn]);

  useEffect(() => {
    // triggers when search parameters exist
    const searchParams = getStateFromUrl(location);
    if (!isEmpty(searchParams)) {
      if (searchParams.date_range) {
        setDate(getDateFromSearchParams(searchParams));
        setReservationCreationError(null);
      }
      if (loggedIn && searchParams.create_reservation === 'true') {
        let formProps = {};
        if (searchParams.add_new_vessel === 'true') {
          formProps = {
            ...formProps,
            hull_type: searchParams.hull_type,
            length: searchParams.length,
            beam: searchParams.beam,
            draft: searchParams.draft,
            vessel_name: searchParams.vessel_name
          };
        }
        handleSubmit({
          formProps,
          addNewFromSearch: searchParams.add_new_vessel === 'true'
        }).then(res => {
          const today = moment().startOf('day');
          const vessel_type = 'boater';
          const reservationVessel = formProps;

          const reservationCreated = {
            reservation_id: res.id,
            marina_id: marina_id,
            marina_name: marina.name,
            marina_country: marina.country,
            marina_association: marina.association.name,
            number_of_nights: moment(res.end_date).diff(
              moment(res.start_date),
              'days'
            ),
            hull_type: reservationVessel.hull_type,
            vessel_loa: reservationVessel.length,
            vessel_beam: reservationVessel.beam,
            vessel_draft: reservationVessel.draft,
            vessel_name: reservationVessel.name,
            origin: location.state && location.state.from === 'top_marinas' ? 'top_marinas' : origin,
            price: !isEmpty(calculatedPrice)
              ? calculatedPrice.total_to_pay
              : '**',
            vessel_type,
            redeem_nights: 'n/a',
            days_before: moment(res.start_date).diff(today, 'days'),
            promotion_type: getFormattedPopeyePromotionType({
              promotions,
              start_date: res.start_date,
              end_date: res.end_date
            })
          };
          sendToAmplitude({
            eventProperties: reservationCreated,
            eventName: 'reservation_created'
          });
        });
      }
    } else {
      if (date.setStartDate || date.setEndDate) {
        setDate({
          setStartDate: null,
          setEndDate: null
        });
        setReservationCreationError(null);
      }
    }
  }, [loggedIn, location]);

  const onChangeDate = setDateValue => {
    if (reservation_limits["short"].max_nights) {
      const startDateMoment = moment(setDateValue.setStartDate, 'DD/MM/YYYY');
      const endDateMoment = moment(setDateValue.setEndDate, 'DD/MM/YYYY');
      if (
        setDateValue.setStartDate &&
        setDateValue.setEndDate &&
        endDateMoment.diff(startDateMoment, 'days') >
          reservation_limits["short"].max_nights
      ) {
        const startDateMomentCopy = moment(startDateMoment);
        setDateValue.setEndDate = startDateMomentCopy
          .add(reservation_limits["short"].max_nights, 'days')
          .format('DD/MM/YYYY');
      }
    }

    if (
      setDateValue.setStartDate !== null &&
      setDateValue.setEndDate !== null
    ) {
      setFocusedDateInput(null);
    }
    setStartDateInvalid(false);
    setEndDateInvalid(false);
    setDateError(null);
    setDate(setDateValue);
    setReservationCreationError(null);
    const isDateChanged =
      setDateValue.setStartDate !== date.setStartDate ||
      setDateValue.setEndDate !== date.setEndDate;
    if (isDateChanged) {
      const eventProperties = {
        ...amplitudeData,
        type: 'date_changed',
        boat_type: !loggedIn
          ? 'guest'
          : !emptyVesselSelected
          ? getVesselType({
              vessel: vessels.filter(({ id }) => id === selectedBoaterVessel)[0]
            })
          : 'boater'
      };
      sendToAmplitude({
        eventProperties,
        eventName: 'reservation_widget_interaction'
      });
    }
  };

  const handleResultCancel = useCallback(() => {
    setInfoDialogShown(false);
  }, []);

  const handleSubmit = async ({ formProps, addNewFromSearch }) => {
    const currentValues = { ...valuesStateRef.current };
    if (reservationId) {
      // the request is already sent
      return Promise.reject();
    }
    if (!addNew && !addNewFromSearch && isHomeMarinaReservationAttempt) {
      // prevent reservation to home marina
      setInfoDialogShown(true);
      return Promise.reject();
    }

    let data = {
      start_date: moment(date.setStartDate, 'DD/MM/YYYY').format('YYYY-MM-DD'),
      end_date: moment(date.setEndDate, 'DD/MM/YYYY').format('YYYY-MM-DD'),
      marina_id,
      attachment_ids: attachmentIds
    };

    /**
     *  If came from marina widget = should not have commission
     *  NOTE: The user can abuse the system if he founds out origin=widget
     *  removes commissions
     */
    const searchParams = getStateFromUrl(location);
    if (searchParams.origin && searchParams.origin.includes('widget'))
      data.remove_commission = true;

    if (
      isTEMConnected &&
      marina.association.code === 'TEM' &&
      calculatedPrice
    ) {
      const { requested_redeem_nights_amount } = formProps;
      data.requested_redeem_nights_amount = isNil(
        requested_redeem_nights_amount
      )
        ? calculatedPrice.max_nights_to_redeem
        : requested_redeem_nights_amount;
    }

    if (addNew || addNewFromSearch) {
      const { hull_type, length, beam, draft, vessel_name: name } = formProps;

      data = {
        ...data,
        create_vessel: true,
        vessel: {
          hull_type,
          length,
          beam,
          draft,
          name
        }
      };
    } else {
      if (!currentValues.vessel_id) {
        return Promise.reject();
      }
      const { vessel_name: name } = formProps;
      data = { ...data, vessel: { id: currentValues.vessel_id, name } };
    }

    const method = instant_booking ? 'createInstant' : 'create';

    try {
      const res = await reservationService[method](data);
      if (res && res.error_type) {
        const { error_type, description, error_code, params } = res;
        let rejectError = t('ERROR_CREATING_REQUEST');
        if (error_type === 'CONFLICT') {
          if (
            description ===
            'There is already instant booked reservation with the same given data'
          ) {
            setStartDateInvalid(true);
            setEndDateInvalid(true);
            setDateError('SAME_DATES_INSTANT_BOOKING');
          } else if (
            description ===
            "The marina doesn't have the instant booking feature enabled"
          ) {
            message.error(t('MARINA_NOT_APPROVED_FOR_INSTANT_BOOK'));
          } else if (
            description ===
            'Found reservation within the given start_date and end_date - can not create duplicate reservations'
          ) {
            setReservationCreationError(
              t('DUPLICATE_RESERVATION_ERROR', { marina_name: marina.name })
            );
            rejectError = 'DUPLICATE_RESERVATION_ERROR';
          } else if (error_code === "RESERVATION_SETTINGS_MAX_DURATION_LIMIT") {
            const max_duration = params?.max_nights;

            message.error(t('RESERVATION_SETTINGS_MAX_DURATION_ERROR', { max_duration }));

          } else if (error_code === "RESERVATION_SETTINGS_MAX_DAYS_IN_ADVANCE_LIMIT") {
            const max_days_in_advance = params?.max_days_before;

            message.error(t('RESERVATION_SETTINGS_MAX_DAYS_IN_ADVANCE_ERROR', { max_days_in_advance }));

          } else if (error_code === "RESERVATION_SETTINGS_MAX_DRAFT_LIMIT") {
            const max_size_draft = params?.max_vessel_draft;

            message.error(t('RESERVATION_SETTINGS_MAX_DRAFT_ERROR', { max_size_draft }));

          } else if (error_code === "RESERVATION_SETTINGS_MAX_BEAM_LIMIT") {
            const max_size_beam = params?.max_vessel_beam;

            message.error(t('RESERVATION_SETTINGS_MAX_BEAM_ERROR', { max_size_beam }));

          } else if (error_code === "RESERVATION_SETTINGS_MAX_LOA_LIMIT") {
            const max_size_loa = params?.max_vessel_length;

            message.error(t('RESERVATION_SETTINGS_MAX_LOA_ERROR', { max_size_loa }));

          } else {
            message.error(t('ERROR_CREATING_REQUEST'));
          }
        }

        setReservationId(null);
        return Promise.reject(rejectError);
      } else {
        setSuccessOpened(true);

        setReservationId(res.id);

        // clear search params (avoid send request again after page reloading)
        setTimeout(() => {
          history.replace({
            pathname: location.pathname,
            search: setStateToUrl({ is_reservation_creation: true })
          });
        }, 0);

        //Amplitude event
        getStatisticsForBoater().then(statistics =>
          updateUserProperties({ boater: user, statistics })
        );

        if (instant_booking) {
          try {
            // eslint-disable-next-line no-use-before-define
            window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
            // eslint-disable-next-line no-use-before-define
            window.dataLayer.push({
              ecommerce: {
                purchase: {
                  actionField: {
                    id: res.id // Transaction ID. Required for purchases and refunds.
                  },
                  products: [
                    {
                      name: marina.name,
                      price: calculatedPrice.total_to_pay
                    }
                  ],
                  marina_name: marina.name || '-',
                  start_date: data.start_date,
                  end_date: data.end_date,
                  country: marina.country || '-',
                  hull_type: currentValues.hull_type,
                  vessel_loa: currentValues.length,
                  vessel_beam: currentValues.beam,
                  vessel_draft: currentValues.draft,
                  vessel_name: currentValues.vessel_name,
                  number_of_nights:
                    moment(data.end_date).diff(
                      moment(data.start_date),
                      'days'
                    ) || '-',
                  price: calculatedPrice.total_to_pay
                }
              },
              eventCallback: function () {
                // document.location = 'checkout.html';
              }
            });
            ReactPixel.track('Purchase', {
              currency: marina.marina_settings.currency,
              value: calculatedPrice.total_to_pay
            });
          } catch (e) {
            console.error('cannot submit to datalayer', e);
          }
        }
        return Promise.resolve(res);
      }
    } catch (err) {
      return Promise.reject(t('ERROR_CREATING_REQUEST'));
    }
  };

  const handleDrawerVisible = bool => {
    setDrawerVisible(bool);
    if (bool) window.zE.hide();
    else window.zE.show();
  };

  const handleDrawerBack = useCallback(() => {
    if (auth === SIGN_UP_AUTH) {
      handleDrawerVisible(false);
    } else if (auth === SIGN_IN_AUTH) {
      setAuth(SIGN_UP_AUTH);
    } else if (auth === GUEST_RESERVATION) {
      setAuth(SIGN_UP_AUTH);
    }
  }, [auth]);

  const handleDrawerClose = useCallback(() => {
    handleDrawerVisible(false);
  }, []);

  const handleOpenSuccess = useCallback(() => {
    setSuccessOpened(true);
  }, []);

  const handleOk = useCallback(() => {
    let pathname;

    if (loggedIn) {
      // reset after close popup and redirect to the reservation page
      pathname =
        `/${match.params.locale}` + RESERVATION_PAGE + `/${reservationId}`;
      setReservationId(null);
      setTimeout(
        () =>
          history.push({
            pathname,
            state: {
              ref: 'reservation_form',
              from: location.state && location.state.from
            }
          }),
        500
      );
    } else {
      // reset after close popup and clear parameters
      pathname = `/${match.params.locale}`;
      setSuccessOpened(false);
      handleDrawerVisible(false);
      setTimeout(() => {
        // workaround: fix the bug from ant design
        // in some reason body gets overflow:hidden after popup closing:
        // https://github.com/ant-design/ant-design/issues/21539
        document.body.style.overflow = '';
        history.push({ pathname, state: { ref: 'reservation_form' } });
      }, 500);
    }
  }, [loggedIn, reservationId, match.params.locale]);

  const isDateValid = () => {
    if (!date.setStartDate) {
      setStartDateInvalid(true);
      setFocusedDateInput('startDate');
      return false;
    }
    if (!date.setEndDate) {
      setEndDateInvalid(true);
      setFocusedDateInput('endDate');
      return false;
    }
    return true;
  };

  const [isChecking, setIsChecking] = useState(false);

  const handleCheck = async formProps => {
    if(isChecking) return;
    setIsChecking(true);
    //Amplitude event for check price
    const {
      hull_type,
      length,
      beam,
      draft,
      requested_redeem_nights_amount,
      vessel_name
    } = formProps;
    const start_date = moment(date.setStartDate, 'DD/MM/YYYY').format(
      'YYYY-MM-DD'
    );
    const end_date = moment(date.setEndDate, 'DD/MM/YYYY').format('YYYY-MM-DD');

    const reservationCheckPriceEventObj = {
      location,
      marina,
      vessel_name,
      hull_type,
      length,
      beam,
      draft,
      start_date,
      end_date,
      requested_redeem_nights_amount,
      calculatedPrice,
      marinas_viewed_before:
        numberOfMarinasViewed.length === 0
          ? 0
          : numberOfMarinasViewed.length - 1,
      error: true,
      promotions
    };

    if (!isDateValid()) {
      const eventProperties = getFormattedCheckPriceEvent(
        reservationCheckPriceEventObj
      );
      sendToAmplitude({
        eventProperties,
        eventName: 'reservation_check_availability'
      });
      setIsChecking(false);
      return false;
    }

    try {
      window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
      window.dataLayer.push({
        event: 'checkout',
        ecommerce: {
          checkout: {
            products: [
              {
                name: marina.name,
                price: calculatedPrice.total_to_pay
              }
            ],
            marina_name: marina.name || '-',
            start_date: start_date,
            end_date: end_date,
            country: marina.country || '-',
            hull_type: hull_type || '-',
            vessel_loa: length || '-',
            vessel_beam: beam || '-',
            vessel_draft: draft || '-',
            vessel_name: vessel_name || '-',
            number_of_nights:
              moment(end_date).diff(moment(start_date), 'days') || '-',
            price: calculatedPrice.total_to_pay
          }
        },
        eventCallback: function () {
          setIsChecking(false);
          // document.location = 'checkout.html';
        }
      });
    } catch (e) {
      console.error('cannot submit to datalayer', e);
    }

    if (!loggedIn) {
      setValues({ ...values, ...formProps });
      setAuth(SIGN_UP_AUTH);
      handleDrawerVisible(true);
      const eventProperties = getFormattedCheckPriceEvent(
        reservationCheckPriceEventObj
      );
      sendToAmplitude({
        eventProperties,
        eventName: 'reservation_check_availability'
      });
    } else {
      // update vessel details if there is some of them are missing
      let vesselValues = { ...values };
      delete vesselValues.vessel_id;
      delete vesselValues.requested_redeem_nights_amount;
      if (!addNew && checkEmptyProps({ ...vesselValues }).length) {
        const { vessel_id, vessel_name: name, hull_type } = formProps;
        const vesselData = {
          id: vessel_id,
          beam,
          draft,
          length,
          name,
          hull_type
        };
        const res = await boaterService.update({ vessel: vesselData });
        if (res && res.error_type) {
          message.error(t('ERROR_CREATING_REQUEST'));
          const eventProperties = getFormattedCheckPriceEvent(
            reservationCheckPriceEventObj
          );
          sendToAmplitude({
            eventProperties,
            eventName: 'reservation_check_availability'
          });
        } else {
          await refreshData({ parameters: ['vessels'], vessel_id });
          setEmptyVesselSelected(false);
          setNoNameVesselSelected(false);
        }
      }
      if (!user.emailVerified) {
        const date_range = {
          setStartDate: isMoment(date.setStartDate)
            ? date.setStartDate.format('DD/MM/YYYY')
            : date.setStartDate,
          setEndDate: isMoment(date.setEndDate)
            ? date.setEndDate.format('DD/MM/YYYY')
            : date.setEndDate
        };
        let search = {
          date_range,
          create_reservation: true,
          is_reservation_creation: true
        };
        if (addNew) {
          search = {
            ...search,
            add_new_vessel: true,
            hull_type,
            length,
            beam,
            draft,
            vessel_name
          };
        }

        const cb = () => {
          history.push({
            pathname: location.pathname,
            search: setStateToUrl(search)
          });
          setModalCloseCallback({});
        };
        setModalCloseCallback({ cb });

        onVerify({
          force: true,
          additionalSearch: setStateToUrl(search),
          currentUser: user
        });
        history.push({
          pathname: location.pathname,
          search: setStateToUrl({ date_range })
        });
        const eventProperties = getFormattedCheckPriceEvent(
          reservationCheckPriceEventObj
        );
        sendToAmplitude({
          eventProperties,
          eventName: 'reservation_check_availability'
        });
      } else {
        handleSubmit({ formProps })
          .then(res => {
            const eventProperties = getFormattedCheckPriceEvent({
              ...reservationCheckPriceEventObj,
              error: false
            });
            sendToAmplitude({
              eventProperties,
              eventName: 'reservation_check_availability'
            });
            let reservationVessel;
            let boat_type;
            if (addNew) {
              boat_type = 'boater';
              reservationVessel = {
                beam,
                draft,
                length,
                name,
                hull_type
              };
            } else {
              reservationVessel = vessels.filter(
                ({ id }) => id === res.vessel_id
              )[0];

              boat_type = getVesselType({
                vessel: reservationVessel
              });
            }

            const today = moment().startOf('day');

            const reservationCreated = {
              reservation_id: res.id,
              marina_id: marina_id,
              marina_name: marina.name,
              marina_country: marina.country,
              marina_association: marina.association.name,
              number_of_nights: moment(end_date).diff(
                moment(start_date),
                'days'
              ),
              hull_type: reservationVessel.hull_type,
              boat_loa: reservationVessel.length,
              boat_beam: reservationVessel.beam,
              boat_draft: reservationVessel.draft,
              vessel_name: reservationVessel.name,
              marinas_viewed_before:
                numberOfMarinasViewed.length === 0
                  ? 0
                  : numberOfMarinasViewed.length - 1,
              origin: location.state && location.state.from === 'top_marinas' ? 'top_marinas' : origin,
              price: !isEmpty(calculatedPrice)
                ? calculatedPrice.total_to_pay
                : '**',
              boat_type,
              redeem_nights: !isNil(requested_redeem_nights_amount)
                ? requested_redeem_nights_amount
                : 'n/a',
              days_before: moment(start_date).diff(today, 'days'),
              promotion_type: getFormattedPopeyePromotionType({
                start_date,
                end_date,
                promotions
              })
            };
            sendToAmplitude({
              eventProperties: reservationCreated,
              eventName: 'reservation_created'
            });
            setNumberOfMarinasViewed([]);
            setIsChecking(false);
          })
          .catch(error => {
            setIsChecking(false);
            const eventProperties = getFormattedCheckPriceEvent({
              ...reservationCheckPriceEventObj,
              duplicate: error === 'DUPLICATE_RESERVATION_ERROR'
            });
            sendToAmplitude({
              eventProperties,
              eventName: 'reservation_check_availability'
            });
          });
      }
    }
  };

  const onChangeMissingVesselInfo = details => {
    const { type, value } = details;
    const vesselMissingDetailsCopy = { ...vesselMissingDetails };
    vesselMissingDetailsCopy[type] = value;
    setVesselMissingDetails(vesselMissingDetailsCopy);
    const eventProperties = {
      ...amplitudeData,
      type,
      boat_type: !loggedIn
        ? 'guest'
        : !emptyVesselSelected
        ? getVesselType({
            vessel: vessels.filter(({ id }) => id === selectedBoaterVessel)[0]
          })
        : 'boater'
    };
    sendToAmplitude({
      eventProperties,
      eventName: 'reservation_widget_interaction'
    });
  };

  const onChangeRequestedRedeemNightsAmount = requestedAmount => {
    const eventProperties = {
      ...amplitudeData,
      type: 'redeem_nights',
      boat_type: 'transeurope member'
    };
    setRequestedRedeemNightsAmount(requestedAmount);
    sendToAmplitude({
      eventProperties,
      eventName: 'reservation_widget_interaction'
    });
  };

  const getFormSchema = () => {
    if (!loggedIn || addNew) {
      return vesselReserveWithNameSchema;
    } else if (emptyVesselSelected || noNameVesselSelected) {
      if (emptyVesselSelected && !noNameVesselSelected) {
        return vesselReserveSchema;
      } else {
        return vesselReserveWithNameSchema;
      }
    } else {
      return null;
    }
  };

  return (
    <Fragment>
      {!!instant_booking && !!pricing_link && (
        <DownloadButtonWrapper>
          <Button
            className='download_tariff_button'
            htmlType='button'
            onClick={e => onDownload(e, pricing_link, t('MARINA_TARIFF'))}
            type='link'
          >
            {t('MARINA_TARIFF')}
          </Button>
        </DownloadButtonWrapper>
      )}

      <Wrapper className='form-container'>
        <Formik
          innerRef={reservationFormRef}
          enableReinitialize
          initialValues={values}
          onSubmit={handleCheck}
          validationSchema={getFormSchema()}
        >
          {props => (
            <RenderReservationForm
              {...props}
              isChecking={isChecking}
              history={history}
              location={location}
              startDateInvalid={startDateInvalid}
              endDateInvalid={endDateInvalid}
              focusedDateInput={focusedDateInput}
              onCloseDatePicker={() => setFocusedDateInput(null)}
              onChangeDate={onChangeDate}
              date={date}
              calculatedPrice={calculatedPrice}
              redeemNightsOptions={redeemNightsOptions}
              datePickerWithPortal={match.path.includes(
                `${MARINA_PAGE}/:id${MESSAGES_PAGE}`
              )}
              isMessaging={isMessaging}
              isTEMConnected={isTEMConnected}
              marina={marina}
              dateError={dateError}
              match={match}
              user={user}
              vesselHullTypes={vesselHullTypes}
              selectRef={selectRef}
              open={open}
              addNew={addNew}
              vesselOptions={vesselOptions}
              isTEMMarina={marina.association.code === 'TEM'}
              onSelect={option => {
                // That is if a selectedBoaterVessel is changed to existed one.
                if (selectedBoaterVessel !== option) {
                  const eventProperties = {
                    ...amplitudeData,
                    type: 'vessel_changed',
                    vessel_type: getVesselType({
                      vessel: vessels.filter(({ id }) => id === option)[0]
                    })
                  };
                  sendToAmplitude({
                    eventProperties,
                    eventName: 'reservation_widget_interaction'
                  });
                }
                setOpen(false);
                setAddNew(false);
                setVesselMissingDetails({});
                setReservationCreationError(null);
                setTimeout(() => {
                  if (selectRef && selectRef.current) {
                    selectRef.current.blur();
                  }
                }, 300);
                const currentVessel = vessels.filter(
                  ({ id }) => id === option
                )[0];
                const {
                  hull_type,
                  length,
                  draft,
                  beam,
                  name: vessel_name
                } = currentVessel;
                props.setFieldValue('vessel_id', option);
                props.setFieldValue('vessel_name', vessel_name);
                props.setFieldValue('hull_type', hull_type);
                props.setFieldValue('length', length);
                props.setFieldValue('draft', draft);
                props.setFieldValue('beam', beam);
                selectBoaterVessel(option);
              }}
              onAddClick={() => {
                props.setFieldValue('vessel_id', null);
                props.setFieldValue('vessel_name', '');
                props.setFieldValue('hull_type', null);
                props.setFieldValue('length', '');
                props.setFieldValue('draft', '');
                props.setFieldValue('beam', '');
                setOpen(false);
                setAddNew(true);
                selectBoaterVessel(undefined);
                setVesselMissingDetails({});
                setIsTEMConnected(false);
                setIsHomeMarinaReservationAttempt(false);
                setReservationCreationError(null);
                const eventProperties = {
                  ...amplitudeData,
                  type: 'add_new_vessel',
                  boat_type: 'boater'
                };
                sendToAmplitude({
                  eventProperties,
                  eventName: 'reservation_widget_interaction'
                });
              }}
              emptyVesselSelected={emptyVesselSelected}
              noNameVesselSelected={noNameVesselSelected}
              onFocus={() => setOpen(true)}
              onBlur={() => setOpen(false)}
              onChangeMissingVesselInfo={onChangeMissingVesselInfo}
              onChangeRequestedRedeemNightsAmount={
                onChangeRequestedRedeemNightsAmount
              }
              reservation_limits={reservation_limits}
              promotions={promotions}
            />
          )}
        </Formik>
        {!!reservationCreationError && (
          <Error>{reservationCreationError}</Error>
        )}
      </Wrapper>

      <ReservationResults
        successOpened={successOpened}
        instant_booking={instant_booking}
        handleOk={handleOk}
        infoDialogShown={infoDialogShown}
        handleCancel={handleResultCancel}
        marinaName={name}
      />

      <WizardDrawer
        visible={drawerVisible}
        auth={auth}
        handleBack={handleDrawerBack}
        handleClose={handleDrawerClose}
        handleOpenSuccess={handleOpenSuccess}
        backButtonText={t('BACK')}
        values={values}
        date={date}
        setAuth={setAuth}
        calculatedPrice={calculatedPrice}
      />
    </Fragment>
  );
};

export default withRouter(ReservationForm);
